import { Flex, HStack, Image, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import Papa from 'papaparse';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CheckIcon } from '@chakra-ui/icons';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import Spinner from 'components/Spinner';
import { Flags } from 'models/Global.interface';
import {
  getNoheadersStatus,
  getSelectedAreaOfTable,
  getSelectedDataFileDetails,
  getSelectedVersionDetails,
  getSingleDataImportFiles,
  resetDataImport,
  setDataSourceApiPreviewEnabled,
  setDistinctCSV,
  setTableHeaderName,
} from 'redux/DataImportSlice';
import apiService from 'services';
import { readDataFile } from 'services/s3Service';
import { AppDispatch } from 'utils/GlobalHelpers';
import {
  DATA_IMPORT_PREVIEW_ROW_LIMIT,
  getDataImportPreviewNoticeText,
} from 'utils/dataImportPreviewNotice';
import { isDataSourceApiPreviewEnabled } from 'utils/dataSourceApiPreviewFlag';

import blankTable from '../../../assets/blank_table_icon.png';
import './DataImportTable.scss';

const textStyles = {
  fontSize: 'sm',
  color: 'rgba(255, 255, 255, 1)',
  fontFamily: 'regular_roboto',
  fontWeight: '500',
};

export interface Properties {
  SelectedDataFileDetails?: any;
  flags?: Flags;
}

const buildDistinctValues = (headers: string[], rows: any[], noHeaders: boolean) => {
  if (noHeaders) {
    const array: string[] = new Array(26)
      .fill(1)
      .map((_: string, index: number) => String.fromCodePoint(65 + index));
    const newHeaders = headers.map((item: string, headerIndex: number) => ({
      current: `${headers[headerIndex]}`,
      modified: `Column ${array[headerIndex]}`,
      distinctValues: [] as any[],
    }));
    rows.forEach((row: any) => {
      newHeaders.forEach((header: any) => {
        if (row[header.current]) {
          header.distinctValues = Array.from(
            new Set([...header.distinctValues, row[header.current]]),
          );
        }
      });
    });
    return newHeaders;
  }

  const newHeaders = headers.map((item: string) => ({
    current: `${item}`,
    modified: `${item}`,
    distinctValues: [] as any[],
  }));
  rows.forEach((row: any) => {
    newHeaders.forEach((header: any) => {
      if (row[header.current]) {
        header.distinctValues = Array.from(
          new Set([...header.distinctValues, row[header.current]]),
        );
      }
    });
  });
  return newHeaders;
};

const DataImportDetails = ({ flags }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const { modelId } = useParams();
  const [csvData, setCsvData] = useState<any>([]);
  const [headerNames, setHeaderNames] = useState<string[]>([]);
  const itemsPerPage = 200;
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedData, setLoadedData] = useState<any>([]);
  const [loader, setLoader] = useState(false);
  const SelectedDataFileDetails = useSelector(getSelectedDataFileDetails);
  const selectedVersionDetails = useSelector(getSelectedVersionDetails);
  const [fileUrl, setFileURL] = useState('');
  const [previewHasMore, setPreviewHasMore] = useState(false);
  const [totalRows, setTotalRows] = useState<number | null>(null);
  const [previewReady, setPreviewReady] = useState(false);
  const dataSourceId = SelectedDataFileDetails?.id;
  const useApiPreview = isDataSourceApiPreviewEnabled(flags);
  const versionId = selectedVersionDetails?.id;
  const tableSelecetedAreas = useSelector(getSelectedAreaOfTable) || {};
  const noHeaders = useSelector(getNoheadersStatus);
  const getSourceDetails = async () => {
    if (dataSourceId && modelId) {
      await dispatch(getSingleDataImportFiles({ modelId, fileId: dataSourceId }));
    }
  };

  // Clean Up function when components destroyed
  useEffect(() => {
    const cleanup = () => {
      // Clear csvData when the component unmounts
      setFileURL('');
      dispatch(resetDataImport());
      setLoadedData([]);
      setCsvData([]);
    };

    return cleanup;
  }, []);
  const [customHeaders, setCustomHeaders] = useState<any[]>(headerNames ?? []);
  const [customCSV, setCustomCSV] = useState<any[]>(csvData ?? []);

  // Function for creating addding custom header and creating unique values for every column

  useEffect(() => {
    dispatch(setDataSourceApiPreviewEnabled(useApiPreview));
  }, [dispatch, useApiPreview]);

  useEffect(() => {
    if (headerNames.length === 0 || csvData.length === 0) {
      return;
    }

    const distinctCSV = buildDistinctValues(headerNames, csvData, noHeaders);
    const newHeaders = distinctCSV.map(({ current, modified }) => ({ current, modified }));

    setCustomHeaders(newHeaders);
    setLoadedData([]);
    setCurrentPage(1);
    setCustomCSV(csvData);
    dispatch(setDistinctCSV(distinctCSV));
  }, [noHeaders, headerNames, csvData, dispatch]);

  useEffect(() => {
    if (SelectedDataFileDetails?.deatsilsPage === false) {
      setHeaderNames([]);
      setLoadedData([]);
    }
  }, [SelectedDataFileDetails]);

  useEffect(() => {
    if (useApiPreview) {
      return;
    }
    if (selectedVersionDetails?.s3_file_url) {
      readDataFile(selectedVersionDetails?.s3_file_url.split('/').pop(), 'blox-data-import').then(
        (url: any) => {
          setFileURL(url);
        },
      );
    }
  }, [selectedVersionDetails?.s3_file_url, useApiPreview]);

  useEffect(() => {
    if (!useApiPreview || !dataSourceId || !versionId) {
      setPreviewReady(false);
      return () => {};
    }

    let cancelled = false;
    setLoader(true);
    setPreviewReady(false);
    setCsvData([]);
    setHeaderNames([]);
    setTotalRows(null);
    setPreviewHasMore(false);

    apiService
      .getVersionPreview(dataSourceId, versionId, {
        rows: DATA_IMPORT_PREVIEW_ROW_LIMIT,
        ...(noHeaders ? { missing_header: true } : {}),
      })
      .then((response) => {
        if (cancelled) return;
        const payload = response.data;
        const columns = payload?.columns || [];
        const rows = payload?.rows || [];
        setHeaderNames(columns);
        dispatch(setTableHeaderName(columns));
        setCsvData(rows);
        setTotalRows(payload?.total_rows ?? null);
        setPreviewHasMore(Boolean(payload?.has_more));
        setPreviewReady(true);
        setLoader(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoader(false);
        setPreviewReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, [useApiPreview, dataSourceId, versionId, noHeaders, dispatch]);
  useEffect(() => {
    getSourceDetails();
  }, [dataSourceId]);

  // parsing data of CSV file
  const parseFileData = (data: any) => {
    setLoader(true);
    const config = {
      worker: true,
      header: true,
      stream: true,
      chunkSize: 1024,
      complete: (result: any) => {
        setHeaderNames(result.meta.fields || []);
        dispatch(setTableHeaderName(result?.meta?.fields));
        setCsvData(result.data || []);
        setLoader(false);
      },
    };

    fetch(data)
      .then((response) => response.text())
      .then((data1: any) => {
        Papa.parse(data1, config);
      });
  };
  useEffect(() => {
    if (fileUrl) {
      setCurrentPage(1);
      setCustomCSV([]);
      dispatch(setDistinctCSV([]));
      setHeaderNames([]);
      setCsvData([]);
      setLoadedData([]);
      setCustomHeaders([]);
      parseFileData(fileUrl);
    }
  }, [fileUrl]);
  const handleScroll = (event: any) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;

    // Calculate the halfway point
    const halfwayPoint = (scrollHeight - clientHeight) / 1.2;

    if (scrollTop >= halfwayPoint) {
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    if (customCSV) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const newData = customCSV && customCSV.slice(startIndex, endIndex);

      // Append new data to the previously loaded data
      setLoadedData((previousData: any) => [...previousData, ...newData]);
    }
  }, [currentPage, customCSV]);

  useEffect(() => {
    return () => {
      setFileURL('');
      setCsvData([]);
      setLoadedData([]);
    };
  }, []);

  const showBlankState = useApiPreview ? !previewReady && !loader : !fileUrl;
  const previewNotice =
    useApiPreview && previewReady
      ? getDataImportPreviewNoticeText(csvData.length, previewHasMore, totalRows)
      : null;

  if (showBlankState) {
    return (
      <Flex py={3}>
        <Image src={blankTable} />
      </Flex>
    );
  }
  return (
    <>
      {previewNotice && (
        <Text fontSize='sm' color='rgba(255,255,255,0.7)' mb={2}>
          {previewNotice}
        </Text>
      )}
      <Flex h={'100%'} width='100%' align={'baseline'} my={'1rem'}>
        <HStack h={'70vh'} w={'full'} overflowY='auto' overflowX={'auto'}>
          {loader ? (
            <Flex
              height={'400px'}
              direction='column'
              alignItems='center'
              justifyContent='center'
              justify='center'
              align='center'
              w={'full'}
            >
              <Spinner styleProperties={{ my: '4vh' }} />
            </Flex>
          ) : (
            <Flex
              h={'100%'}
              w={'100%'}
              display={'block'}
              // whiteSpace={'nowrap'}
              // maxHeight={'400px'}
              overflowY='auto'
              overflowX={'auto'}
              mb={{ base: 4.75, md: 0 }}
              mt={{ base: 0, md: 0 }}
              className='sm-scroll indicator-table'
              onScroll={handleScroll}
              borderRadius='1rem'
              borderBottom={'1px solid rgba(255, 255, 255, 0.30)'}
            >
              <Flex>
                <Table variant='simple'>
                  <Thead position='sticky' top={'-1px'} borderRadius={'11px'}>
                    <Tr bgColor={'#69B4B8'}>
                      {customHeaders.map((header: any, index: any) => (
                        <Th
                          {...textStyles}
                          py={3}
                          bgColor='#4BB6B9'
                          overflow='hidden'
                          whiteSpace='nowrap'
                          textOverflow='ellipsis'
                          color={'#ffff'}
                          key={`${header.modified} ${index} `}
                          textAlign={'center'}
                          textTransform='capitalize'
                          border={'1px solid rgba(255, 255, 255, 0.30)'}
                        >
                          <Flex justify={'space-between'} align={'center'} gap={3}>
                            {header.modified}
                            {tableSelecetedAreas &&
                              Object.values(tableSelecetedAreas).includes(header.current) && (
                                <CheckIcon />
                              )}
                          </Flex>
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody bg={'transparent'}>
                    {loadedData.map((rowData: any, rowIndex: any) => (
                      <Tr key={rowIndex}>
                        {headerNames.map((header, index) => (
                          <Td
                            {...textStyles}
                            border={'1px solid rgba(255, 255, 255, 0.30)'}
                            py={2}
                            key={index}
                            textAlign={'start'}
                            bgColor={
                              tableSelecetedAreas !== 'undefined' &&
                              Object.values(tableSelecetedAreas).length > 0 &&
                              Object.values(tableSelecetedAreas).includes(header)
                                ? 'rgba(147, 140, 209, 0.20)'
                                : 'none'
                            }
                          >
                            {rowData[header]}
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Flex>
            </Flex>
          )}
        </HStack>
      </Flex>
    </>
  );
};

export default withLDConsumer()(DataImportDetails);
